// Name→roster matcher (AMB-372 / F2, module 7).
//
// Resolve an extracted speaker/owner string to a roster member id ONLY when confident —
// exact id, full name, first name, or initials. A genuine outsider keeps their raw name
// (trimmed), never dropped or mangled. Conservative on purpose: a wrong match is worse than
// an honest raw name. Pure, so generation, the backfill, and display can all share it.

interface RosterLike {
  id: string;
  name: string;
  initials: string;
}

const norm = (s: string): string => s.trim().toLowerCase();
const firstToken = (s: string): string => s.trim().split(/[\s/(),]+/).filter(Boolean)[0] || "";

/** Returns a member id when matched, else the raw extracted name (trimmed). "" for empty. */
export function matchName(extracted: string | null | undefined, roster: RosterLike[]): string {
  const raw = (extracted || "").trim();
  if (!raw) return "";
  const n = norm(raw);

  // Exact id or full name.
  const exact = roster.find((m) => m.id === n || norm(m.name) === n);
  if (exact) return exact.id;

  // First name (handles "First / Org", "First (Org)", "First Last" where last is unknown).
  const first = norm(firstToken(raw));
  const byFirst = roster.find((m) => norm(firstToken(m.name)) === first);
  if (byFirst) return byFirst.id;

  // Initials (e.g. "KG", "rm") — only when it's a 2-letter token, so a real short name
  // doesn't get coerced to someone's initials.
  if (/^[a-z]{2}$/.test(n)) {
    const byInitials = roster.find((m) => norm(m.initials) === n);
    if (byInitials) return byInitials.id;
  }

  return raw; // genuine outsider — keep the name as written
}
